import clsx from 'clsx';
import styled from 'styled-components';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';

import { customMedia, focusVisible } from 'src/styles';
import { IMAGES } from 'src/assets';
import Icon from '../Icon';

const Container = styled.div`
  display: grid;
  grid-template-columns: 10fr 2fr;
  grid-template-areas: 'preview-info preview-image';
  grid-template-rows: minmax(0.625rem, 6.25rem) auto;

  ${customMedia.lessThan('lg')`
    display: grid;
    grid-auto-flow: column;
    grid-template-areas:
    "preview-image"
    "preview-info";
    grid-template-columns: auto;
  `}
`;

const LinkPreviewImage = styled.img`
  object-fit: cover;
  max-height: 9.375rem;
  border-bottom-right-radius: var(--border-radius-lg);
  border-top-right-radius: var(--border-radius-lg);
  ${customMedia.lessThan('lg')`
    border-top-left-radius: var(--border-radius-lg);
    border-top-right-radius: var(--border-radius-lg);
    border-bottom-right-radius: unset;
  `}
`;

const InfoContainer = styled.p`
  grid-area: preview-info;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const LinkPreviewImageContainer = styled.div`
  grid-area: preview-image;
  border-bottom-right-radius: var(--border-radius-lg);
  border-top-right-radius: var(--border-radius-lg);

  ${customMedia.lessThan('lg')`
    border-top-left-radius: var(--border-radius-lg);
    border-top-right-radius: var(--border-radius-lg);
    border-bottom-right-radius: unset;
  `}
`;

const IconContainer = styled.div`
  top: var(--spacer-3);
  right: var(--spacer-3);
  width: 2.25rem;
  height: 2.25rem;
  z-index: 1;
`;

const Link = styled.a`
  ${focusVisible}
`;

export type LinkPreviewProps = {
  link: string;
  linkDomain: string;
  title?: string;
  image?: string;
  className?: string;
  handleRemove?: () => void;
};

const LinkPreview = ({
  link,
  linkDomain,
  title,
  image,
  className,
  handleRemove
}: LinkPreviewProps) => {
  const containerClassName = clsx(
    '_border _border-gray-200 _rounded-lg  _w-full _position-relative',
    className
  );
  return (
    <Container className={containerClassName} data-test="link-preview">
      {handleRemove && (
        <IconContainer
          onClick={() => handleRemove()}
          className="_cursor-pointer _position-absolute _bg-white _p-3 _rounded _d-flex _align-items-center _justify-content-center _text-gray-500"
          data-test="link-preview-remove"
        >
          <Icon icon={faTrash} />
        </IconContainer>
      )}
      <div className="_p-4 _d-flex _flex-column _justify-content-center _text-truncate">
        <div className="_d-flex _mb-1">
          <InfoContainer className="_mb-1 _font-14 _text-gray-900" data-test="link-preview-info">
            <span>{title ? title : linkDomain}</span>
          </InfoContainer>
        </div>
        <p className="_m-0 _text-1 _text-truncate" data-test="link-preview-host">
          {linkDomain}
        </p>
      </div>
      <LinkPreviewImageContainer>
        {image ? (
          <LinkPreviewImage
            src={image}
            data-test="link-preview-image"
            alt={title}
            loading="lazy"
            className="_rounded _w-full _h-full"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = IMAGES.NO_IMAGE;
              currentTarget.style.width = '100%';
              currentTarget.style.height = '100%';
            }}
          />
        ) : (
          <div
            className="_d-flex _align-items-center _justify-content-center _bg-gray-200 _w-full _h-full"
            data-test="link-preview-no-image"
          >
            <Icon className="_text-gray-500" icon={faLink} />
          </div>
        )}
      </LinkPreviewImageContainer>
      <Link
        className="_w-full _h-full _position-absolute"
        href={link}
        aria-label={title}
        target="_blank"
        rel="noreferrer"
      />
    </Container>
  );
};

export default LinkPreview;
